import React, { useState, lazy, Suspense, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Element } from 'react-scroll';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';

import AboutTab from 'Features/Product/components/ProductAboutSection/AboutTab';
import BookDetails from 'Features/Product/components/ProductAboutSection/BookDetails';
import ProductDetails from 'Features/Product/components/ProductAboutSection/ProductDetails';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import SampleDownloads from 'Components/Utility/Sample/SampleDownloads';
import SystemRequirements from 'Components/SystemRequirements/SystemRequirements';
import GiftCardHowToSteps from 'Features/Product/components/ProductAboutSection/GiftCardHowToSteps';
import useViewport from 'Hooks/useViewport';

import 'Features/Product/components/ProductAboutSection/ProductAbout.scss';
import 'Features/Product/components/Utalk/Utalk.scss';

const UtalkLanguageSearch = lazy(() => import(/* webpackChunkName: "UtalkLanguageSearch" */ 'Features/Product/components/Utalk/UtalkLanguageSearch'));
const ProductAboutVrSupport = lazy(() => import(/* webpackChunkName: "ProductAboutVrSupport" */ 'Features/Product/components/ProductAboutSection/ProductAboutVrSupport'));

const ProductAboutSection = ({
  name,
  systems,
  ztormGame,
  catalina,
  product,
}) => {
  const [tab, setTab] = useState('about');

  const currentDrm = ProductRequirements.CurrentDrm(product);
  const dates = ProductRequirements.ReleaseDates(product);
  const platforms = product.platforms;
  const requirements = ProductRequirements.ShowRequirements(product);
  const nintendoProduct = ProductRequirements.ShowNintendo(product);
  const { isBelowLg, isAboveMd } = useViewport();
  const displayType = product.display_type;

  useEffect(() => {
    if (displayType === 'gift-card' && isAboveMd) setTab('about');
  }, [displayType, isAboveMd]);

  const showVr = !isEmpty(product.vr_support);
  const showSysReqs = (platforms.windows || platforms.mac || platforms.linux) && currentDrm !== 'utalk' && product.type !== 'bundle' && !isEmpty(product.platform_specs);
  const showUtalk = currentDrm === 'utalk';
  const showGiftCard = product.display_type === 'gift-card';

  let mobileTabCount = 2;
  if (showVr) mobileTabCount += 1;
  if (showSysReqs) mobileTabCount += 1;
  if (showUtalk) mobileTabCount += 1;
  if (showGiftCard) mobileTabCount += 1;

  let tabLayout = 'two-column';
  if (mobileTabCount === 3) tabLayout = 'three-column';
  if (mobileTabCount === 4) tabLayout = 'four-column';

  const showAboutTabBtn = product.display_type !== 'gift-card' || (product.display_type === 'gift-card' && isBelowLg);

  let filteredDownloads = [];
  if (product.downloads) {
    filteredDownloads = product.downloads.filter(download => !download.is_sample);
  }

  const downloadTypes = [];
  filteredDownloads.forEach((download) => {
    download.files.forEach((file) => {
      if (includes(downloadTypes, file.format)) return;
      if (file.format === 'zip') return;
      downloadTypes.push(file.format);
    });
  });

  let sampleDownload = null;
  if (product.downloads && Array.isArray(product.downloads)) {
    sampleDownload = product.downloads.find(download => download.is_sample);
  }

  let productType = product.type;
  if (product.type === 'game') productType = 'Game';
  if (product.type === 'dlc') productType = 'DLC';
  if (product.type === 'comic') productType = 'Comic';
  if (product.type === 'book') productType = 'Book';
  if (product.type === 'software') productType = 'Software';
  if (currentDrm === 'voucher' || currentDrm === 'redeem') productType = 'Product';
  if (product.slug === 'rewards-week-2023-sign-in-and-win-contest') productType = 'Contest';
  if (product.display_type === 'graphic-novel') productType = 'Graphic Novel';
  if (product.display_type === 'audio') productType = 'Audio';
  if (product.type === 'book' && product.display_type === 'audio') productType = 'Audiobook';
  if (product.type === 'gift-card') productType = 'Gift Card';

  return (
    <>
      <section className="ProductAboutSection product-about-section page-section-margin">
        <Element name="product-about-section" />
        <div className="title-tabs-container">
          <div className="title-container">
            <h2 className="section-title">
              <FormattedMessage
                id="productaboutsection.aboutproduct"
                defaultMessage="About {name}"
                values={{ name }}
              />
            </h2>
          </div>

          <div className={`tabs-container ${tabLayout}`}>
            {showAboutTabBtn &&
              <div className="tab-button-container">
                <button
                  className={classNames('product-tab-button', { active: tab === 'about' })}
                  onClick={() => setTab('about')}
                >
                  <FormattedMessage id="productaboutsection.about" defaultMessage="About" />
                </button>
              </div>
            }

            {product.display_type !== 'gift-card' && isBelowLg &&
              <div className="tab-button-container d-xs-block d-lg-none">
                <button
                  className={classNames('product-tab-button', { active: tab === 'details' })}
                  onClick={() => setTab('details')}
                >
                  <FormattedMessage id="productaboutsection.details" defaultMessage="Details" />
                </button>
              </div>
            }

            {showGiftCard &&
              <div className="tab-button-container d-xs-block d-lg-none">
                <button
                  className={classNames('product-tab-button', { active: tab === 'how-it-works' })}
                  onClick={() => setTab('how-it-works')}
                >
                  <FormattedMessage id="productaboutsection.howitworks" defaultMessage="How It Works" />
                </button>
              </div>
            }

            {showVr &&
              <div className="tab-button-container">
                <button
                  className={classNames('product-tab-button', { active: tab === 'vr' })}
                  onClick={() => setTab('vr')}
                >
                  <FormattedMessage id="productaboutsection.vrsupport" defaultMessage="VR Support" />
                </button>
              </div>
            }

            {showSysReqs &&
              <div className="tab-button-container">
                <button
                  className={classNames('product-tab-button', { active: tab === 'sysreq' })}
                  onClick={() => setTab('sysreq')}
                >
                  <FormattedMessage id="productaboutsection.requirements" defaultMessage="Requirements" />
                </button>
              </div>
            }

            {showUtalk &&
              <div className="tab-button-container">
                <button
                  className={classNames('product-tab-button', { active: tab === 'language' })}
                  onClick={() => setTab('language')}
                >
                  <FormattedMessage id="productaboutsection.language" defaultMessage="Languages" />
                </button>
              </div>
            }
          </div>
        </div>

        <div className="tab-content-container">
          <div className={`about-tab ${tab === 'about' ? 'd-block' : 'd-none'}`}>
            <AboutTab
              product={product}
              currentDrm={currentDrm}
              downloadTypes={downloadTypes}
              sampleDownload={sampleDownload}
            />
          </div>

          <div className={`product-details-tab ${tab === 'details' ? 'd-block' : 'd-none'}`}>
            {sampleDownload &&
              <article>
                <h3 className="section-heading">
                  <FormattedMessage
                    id="productaboutsection.preview.title"
                    defaultMessage="Preview {type}"
                    values={{ type: productType }}
                  />
                </h3>
                <SampleDownloads download={sampleDownload} />
                <hr />
              </article>
            }

            {product.display_type !== 'book' &&
              <ProductDetails
                soldOut={product.sold_out}
                data={{
                  publishers: product.publishers,
                  developers: product.developers,
                  genres: product.genres,
                  collections: product.collections,
                  features: product.features,
                  combo: product.combo,
                  themes: product.themes,
                  playstyles: product.playstyles,
                  categories: product.categories,
                  franchises: product.franchises,
                  systems: requirements.validSystems,
                  platforms: requirements.validPlatforms,
                  catalina: product.catalina,
                  name: product.name,
                  type: product.type,
                  slug: product.slug,
                  age: product.age,
                  languages: product.lang,
                  display_type: product.display_type,
                  steam: product.steam,
                  parent_slug: product.parent_slug,
                  userTags: product.userTags,
                  xboxPlatforms: product.xbox_platforms,
                }}
                orderDate={product.order_date}
                noReleaseDate={product.no_release_date}
                released={dates.releaseDate}
                playableFrom={dates.playableFrom}
                earlyAccess={dates.earlyAccess}
                links={{ url: product.url, steam: product.steam }}
                nintendoProduct={nintendoProduct}
                currentDrm={currentDrm}
                giveaway={product.giveaway}
              />
            }
            {product.display_type === 'book' &&
              <BookDetails
                data={{
                  age: product.age,
                  artists: product.artists,
                  authors: product.authors,
                  files: product.files,
                  isbn: product.isbn,
                  languages: product.lang,
                  pages: product.pages,
                  publishers: product.publishers,
                  collections: product.collections,
                }}
                released={dates.releaseDate}
                links={{ url: product.url, steam: product.steam }}
                currentDrm={currentDrm}
                downloadTypes={downloadTypes}
                type={product.display_type}
              />
            }
          </div>

          {showVr &&
            <div className={`vr-support-tab ${tab === 'vr' ? 'd-block' : 'd-none'}`}>
              <Suspense fallback="div">
                <ProductAboutVrSupport
                  vr={product.vr_support}
                  requirements={product.vr_requirements}
                />
              </Suspense>
            </div>
          }

          {showSysReqs &&
            <div className={`system-requirements-tab ${tab === 'sysreq' ? 'd-block' : 'd-none'}`}>
              <SystemRequirements
                showTitle
                systems={systems}
                requirements={product.platform_specs}
                ztormGame={ztormGame}
                catalina={catalina}
              />
            </div>
          }

          {showUtalk &&
            <div className={`utalk-languages-tab ${tab === 'language' ? 'd-block' : 'd-none'}`}>
              <Suspense fallback="div">
                <UtalkLanguageSearch />
              </Suspense>
            </div>
          }

          {showGiftCard &&
            <div className={`how-it-works-tab ${tab === 'how-it-works' ? 'd-block' : 'd-none'}`}>
              <GiftCardHowToSteps
                externalRedeemLink={product.external_redeem_link}
                redemptionSteps={product.redeem_instruction}
                drm={currentDrm}
              />
            </div>
          }
        </div>
      </section>
    </>
  );
};

ProductAboutSection.propTypes = {
  name: PropTypes.string,
  ztormGame: PropTypes.bool,
  catalina: PropTypes.bool,
  systems: PropTypes.shape({
    windows: PropTypes.bool,
    mac: PropTypes.bool,
    linux: PropTypes.bool,
  }),
  product: PropTypes.shape({}),
};

ProductAboutSection.defaultProps = {
  name: '',
  ztormGame: false,
  catalina: false,
  systems: {
    windows: false,
    mac: false,
    linux: false,
  },
  product: {},
};


export default ProductAboutSection;
